import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line
} from 'recharts';
import { 
  Box, 
  Skeleton, 
  Typography, 
  ToggleButton, 
  ToggleButtonGroup,
  useTheme 
} from '@mui/material';

const ActivityChart = ({ data, loading, chartType = 'bar', onChartTypeChange }) => {
  const theme = useTheme();

  if (loading) {
    return <Skeleton variant="rectangular" height={300} />;
  }

  if (!data || data.length === 0) {
    return (
      <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height={300}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No activity data available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Connect your devices and start tracking to see your progress here
        </Typography>
      </Box>
    );
  }

  // Format data for chart - handle different possible data structures
  const chartData = data.map((item, index) => {
    const day = item.day || item.date || item.label || `Day ${index + 1}`;
    const steps = item.steps || item.value || item.count || 0;
    const target = item.target || item.goal || 10000;
    
    return {
      day: typeof day === 'string' ? day : day.toLocaleDateString?.() || day,
      steps,
      target,
      percentage: target > 0 ? Math.round((steps / target) * 100) : 0
    };
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 1.5,
            boxShadow: 1
          }}
        >
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography 
              key={index}
              variant="body2" 
              sx={{ color: entry.color }}
            >
              {entry.name}: {entry.value?.toLocaleString?.() || entry.value}
              {entry.dataKey === 'steps' && payload[0]?.payload?.percentage && (
                ` (${payload[0].payload.percentage}% of goal)`
              )}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 20, right: 30, left: 20, bottom: 5 }
    };

    if (chartType === 'line') {
      return (
        <LineChart {...commonProps}>
          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
          <XAxis 
            dataKey="day" 
            stroke={theme.palette.text.secondary}
            fontSize={12}
          />
          <YAxis 
            stroke={theme.palette.text.secondary}
            fontSize={12}
          />
          <Tooltip content={<CustomTooltip />} />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="steps" 
            stroke={theme.palette.primary.main}
            strokeWidth={3}
            dot={{ fill: theme.palette.primary.main, strokeWidth: 2, r: 4 }}
            name="Steps"
          />
          <Line 
            type="monotone" 
            dataKey="target" 
            stroke={theme.palette.grey[400]}
            strokeDasharray="5 5"
            dot={false}
            name="Goal"
          />
        </LineChart>
      );
    }

    return (
      <BarChart {...commonProps}>
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
        <XAxis 
          dataKey="day" 
          stroke={theme.palette.text.secondary}
          fontSize={12}
        />
        <YAxis 
          stroke={theme.palette.text.secondary}
          fontSize={12}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend />
        <Bar 
          dataKey="steps" 
          fill={theme.palette.primary.main}
          name="Steps"
          radius={[4, 4, 0, 0]}
        />
        <Bar 
          dataKey="target" 
          fill={theme.palette.grey[300]}
          name="Goal"
          radius={[4, 4, 0, 0]}
        />
      </BarChart>
    );
  };

  return (
    <Box>
      {onChartTypeChange && (
        <Box display="flex" justifyContent="flex-end" mb={2}>
          <ToggleButtonGroup
            value={chartType}
            exclusive
            onChange={(e, newType) => newType && onChartTypeChange(newType)}
            size="small"
          >
            <ToggleButton value="bar">
              Bar Chart
            </ToggleButton>
            <ToggleButton value="line">
              Line Chart
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      )}
      
      <ResponsiveContainer width="100%" height={400}>
        {renderChart()}
      </ResponsiveContainer>
      
      <Box mt={2}>
        <Typography variant="caption" color="text.secondary" align="center" display="block">
          Daily activity for the past {chartData.length} days
        </Typography>
      </Box>
    </Box>
  );
};

export default ActivityChart;